import { useLocation, Link } from 'react-router-dom'
import HomeIcon from '../icons/HomeIcon'
import Calendar from '../icons/Calendar'
import CheckList from '../icons/CheckList'
import ChevronIcon from '../icons/ChevronIcon'
import BookShelf from '../icons/BookShelf'
import { useAuth } from '../../contexts/AuthContext'

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isOpen = false, onClose }: SidebarProps) {
    const location = useLocation();
    const { user } = useAuth();

    const isPathActive = (path: string, exact = true) => {
        if (exact) return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    const handleLinkClick = () => {
        onClose?.();
    };

    return (
        <div
            className={`
                flex flex-col fixed left-0 z-[1000] bg-white py-2 border-r border-gray-300
                w-[245px] items-start px-3
                transition-transform duration-300
                xl:translate-x-0
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
            `}
            style={{ top: '64px', height: 'calc(100vh - 64px)' }}
        >
            <div className="flex flex-col mt-2 gap-1 w-full">
                {/* Home */}
                <Link 
                    to="/"
                    onClick={handleLinkClick}
                    className={`flex flex-row items-center justify-start px-4 py-3 rounded-full transition-colors duration-200 ${
                        isPathActive('/') ? 'font-bold text-black bg-gray-100' : 'text-black hover:bg-gray-100'
                    }`}
                >
                    <HomeIcon className="w-[24px] h-[24px]" />
                    <span className="ml-4 text-base">Home</span>
                </Link>

                {/* Scheduler */}
                <Link 
                    to="/schedule"
                    onClick={handleLinkClick}
                    className={`flex flex-row items-center justify-start px-4 py-3 rounded-full transition-colors duration-200 ${
                        isPathActive('/schedule') ? 'font-bold text-black bg-gray-100' : 'text-black hover:bg-gray-100'
                    }`}
                >
                    <Calendar className="w-[24px] h-[24px]" />
                    <span className="ml-4 text-base">Scheduler</span>
                </Link>

                {user && user.programs.length > 0 && (
                    <div className="my-2 border-t border-gray-200 w-full" />
                )}

                {/* Programs Section */}
                <Link
                    to="/programs"
                    onClick={handleLinkClick}
                    className={`flex flex-row items-center px-4 py-3 rounded-full transition-colors duration-200 ${
                        isPathActive('/programs') ? 'font-bold text-black bg-gray-100' : 'text-black hover:bg-gray-100'
                    } ${user && user.programs.length > 0 ? 'font-semibold' : ''}`}
                >
                    {user && user.programs.length > 0 ? (
                        <>
                            <span className="text-lg mr-1">Programs</span>
                            <ChevronIcon direction="right" className="w-5 h-5" />
                        </>
                    ) : (
                        <>
                            <BookShelf className="w-[24px] h-[24px]" />
                            <span className="ml-4 text-base">Programs</span>
                        </>
                    )}
                </Link>

                {/* User Programs List */}
                {user && user.programs.length > 0 && (
                    <div className="flex flex-col gap-1 mt-1">
                        {[...user.programs].sort((a, b) => {
                            const order = { college: 0, major: 1, minor: 2 };
                            return order[a.type] - order[b.type];
                        }).map((program) => {
                            const initials = program.programId.substring(0, 2).toUpperCase();
                            return (
                                <Link
                                    key={program.programId}
                                    to={`/programs/${program.programId}`}
                                    onClick={handleLinkClick}
                                    className={`flex items-center px-4 py-2 rounded-lg text-base transition-colors duration-200 ${
                                        location.pathname === `/programs/${program.programId}`
                                            ? 'bg-gray-100 text-black font-medium'
                                            : 'text-black hover:bg-gray-50'
                                    }`}
                                    title={program.name}
                                >
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0">
                                        <span className="text-[10px] font-bold text-gray-600">{initials}</span>
                                    </div>
                                    <span className="truncate">
                                        {program.type === 'minor'
                                            ? `${program.programId.split('_')[0]} Minor`
                                            : program.type === 'college'
                                                ? `${program.programId.split('_')[0]} Requirements`
                                                : program.name}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                )}

                {user && user.programs.length > 0 && (
                    <div className="my-2 border-t border-gray-200 w-full" />
                )}

                {/* Plan */}
                <Link 
                    to="/plan"
                    onClick={handleLinkClick}
                    className={`flex flex-row items-center justify-start px-4 py-3 rounded-full transition-colors duration-200 ${
                        isPathActive('/plan') ? 'font-bold text-black bg-gray-100' : 'text-black hover:bg-gray-100'
                    }`}
                >
                    <CheckList className="w-[24px] h-[24px]" />
                    <span className="ml-4 text-base">Plan</span>
                </Link>
            </div>
        </div>
    );
}
